import { useRef, useState, useEffect, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eraser, PenLine, Type } from "lucide-react";

// Emits onChange({ type: 'canvas'|'typed', data: string, hasSignature: bool })
export const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [mode, setMode] = useState("canvas");
  const [typed, setTyped] = useState("");

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
  }, []);

  useEffect(() => {
    if (mode === "canvas") {
      // small delay so layout settles after tab switch
      const t = setTimeout(setupCanvas, 30);
      return () => clearTimeout(t);
    }
  }, [mode, setupCanvas]);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointerPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasDrawn.current = true;
  };

  const endDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
    if (hasDrawn.current) {
      const data = canvasRef.current.toDataURL("image/png");
      onChange({ type: "canvas", data, hasSignature: true });
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
    onChange({ type: "canvas", data: "", hasSignature: false });
  };

  const handleTyped = (val) => {
    setTyped(val);
    onChange({ type: "typed", data: val.trim(), hasSignature: val.trim().length > 0 });
  };

  const clearTyped = () => {
    setTyped("");
    onChange({ type: "typed", data: "", hasSignature: false });
  };

  return (
    <Tabs
      value={mode}
      onValueChange={(v) => {
        setMode(v);
        // reset signature when switching mode
        if (v === "canvas") {
          hasDrawn.current = false;
          onChange({ type: "canvas", data: "", hasSignature: false });
        } else {
          onChange({ type: "typed", data: typed.trim(), hasSignature: typed.trim().length > 0 });
        }
      }}
      className="w-full"
    >
      <TabsList className="grid w-full grid-cols-2 rounded-full bg-slate-100 p-1">
        <TabsTrigger value="canvas" data-testid="sig-tab-draw" className="rounded-full data-[state=active]:bg-[#3556A5] data-[state=active]:text-white">
          <PenLine className="mr-2 h-4 w-4" /> Draw
        </TabsTrigger>
        <TabsTrigger value="typed" data-testid="sig-tab-type" className="rounded-full data-[state=active]:bg-[#3556A5] data-[state=active]:text-white">
          <Type className="mr-2 h-4 w-4" /> Type
        </TabsTrigger>
      </TabsList>

      <TabsContent value="canvas" className="mt-4">
        <div className="relative rounded-2xl border-2 border-dashed border-[#C9A227]/50 bg-white">
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            className="h-40 w-full touch-none rounded-2xl"
            onPointerDown={startDraw}
            onPointerMove={moveDraw}
            onPointerUp={endDraw}
            onPointerLeave={endDraw}
          />
          <span className="pointer-events-none absolute bottom-2 left-4 text-xs text-slate-400">
            Sign with your finger or mouse
          </span>
        </div>
        <Button type="button" variant="ghost" onClick={clearCanvas} data-testid="sig-clear-draw" className="mt-2 text-[#3556A5]">
          <Eraser className="mr-2 h-4 w-4" /> Clear
        </Button>
      </TabsContent>

      <TabsContent value="typed" className="mt-4">
        <Input
          value={typed}
          onChange={(e) => handleTyped(e.target.value)}
          placeholder="Type your full name"
          data-testid="signature-typed-input"
          className="rounded-xl bg-slate-50 p-4 text-base"
        />
        {typed.trim() && (
          <div className="mt-3 rounded-2xl border-2 border-dashed border-[#C9A227]/50 bg-white p-5 text-center">
            <span className="font-signature text-4xl text-[#0F172A]">{typed}</span>
          </div>
        )}
        <Button type="button" variant="ghost" onClick={clearTyped} data-testid="sig-clear-type" className="mt-2 text-[#3556A5]">
          <Eraser className="mr-2 h-4 w-4" /> Clear
        </Button>
      </TabsContent>
    </Tabs>
  );
};

export default SignaturePad;
